import { useQuery } from '@tanstack/react-query'
import {
  PracTestDetailRes,
  PracTestStatRes,
  TestmanageRes,
} from '@/types/practice-test'
import { axiosInstance } from '@/lib/axios'

interface PracTestProps {
  moduleId: string
  page: number
  isPracTest: string
  status: string
  search?: string
  creator_type: string
  class_id: number
}

export const getPracTestStat = async (moduleId: string) => {
  return axiosInstance.get<PracTestStatRes>(
    `/practice-test/${moduleId}/statistics-practice-test`
  )
}

export const getPracTestDetail = async (pracTestId: string) => {
  return axiosInstance.get<PracTestDetailRes>(
    `/practice-test/${pracTestId}/overview`
  )
}

export const getPracTests = async ({
  moduleId,
  page,
  isPracTest,
  status,
  search,
  creator_type,
  class_id,
}: PracTestProps) => {
  const params = new URLSearchParams({
    page: page.toString(),
    limit: '10',
    is_practice: isPracTest,
    status: status,
  })

  if (search) {
    params.append('search', search)
  }

  // Only include creator_type if it's not 'all'
  if (creator_type && creator_type !== 'all') {
    params.append('creator_type', creator_type)
  }

  if (class_id && class_id !== 0) {
    params.append('class_id', class_id.toString())
  }

  return axiosInstance.get<TestmanageRes>(
    `/practice-test/${moduleId}/all-practice-tests?${params.toString()}`
  )
}

export const useGetPracTestStat = (moduleId: string) => {
  const { data, isLoading } = useQuery({
    queryKey: ['prac_test_stat', moduleId],
    queryFn: () => getPracTestStat(moduleId),
    enabled: !!moduleId,
  })

  return { pracTestStat: data?.data, isLoadingPracTestStat: isLoading }
}

export const useGetTests = ({
  moduleId,
  page,
  isPracTest,
  status,
  search,
  creator_type,
  class_id,
}: PracTestProps) => {
  const { data, isLoading } = useQuery({
    queryKey: [
      'tests',
      moduleId,
      page,
      isPracTest,
      status,
      search,
      creator_type,
      class_id,
    ],
    queryFn: () =>
      getPracTests({
        moduleId,
        page,
        isPracTest,
        status,
        search,
        creator_type,
        class_id,
      }),
    enabled: !!moduleId,
  })

  return {
    tests: data?.data.data,
    meta: data?.data.meta,
    isLoadingTests: isLoading,
  }
}

export const useGetPracTestDetail = (pracTestId: string) => {
  const { data, isLoading, refetch } = useQuery({
    queryKey: ['prac_test_detail', pracTestId],
    queryFn: () => getPracTestDetail(pracTestId),
    enabled: !!pracTestId,
  })

  return {
    pracTestStat: data?.data,
    isLoadingPracTestStat: isLoading,
    refetchPracTestStat: refetch,
  }
}
